import type { FeedFormat, MetricView, Platform } from '../api/types'
import { ANALYTICS_PERIODS, moscowToday, validDateRange } from './instagramAnalytics.ts'

export const AUTHOR_GROUPS = [
  ['topic', 'Темы'],
  ['rubric', 'Рубрики'],
  ['hook', 'Заходы'],
  ['role', 'Роли контента'],
  ['cta', 'Призывы к действию'],
] as const
export type AuthorGroupBy = (typeof AUTHOR_GROUPS)[number][0]
export type AuthorScope = 'own' | 'reposts'
export interface AuthorPeriod {
  period_days?: number
  date_from?: string
  date_to?: string
  all_time?: boolean
}
export interface AuthorFilters extends AuthorPeriod {
  format: FeedFormat
  group_by: AuthorGroupBy
  scope: AuthorScope
  metric_view?: MetricView
}
export type AuthorPane = 'portrait' | 'group' | 'evidence' | 'annotation' | 'topic'

/** URL остаётся источником истины. Старые ссылки d=… продолжают открывать период. */
export function authorFilters(params: URLSearchParams, platform: Platform): AuthorFilters {
  const rawFormat = params.get('format') ?? params.get('f')
  const format: FeedFormat = platform === 'instagram' && rawFormat !== 'text' ? 'video' : 'text'
  const scope: AuthorScope = platform === 'telegram' && params.get('scope') === 'reposts' ? 'reposts' : 'own'
  const group = params.get('group_by')
  const group_by = AUTHOR_GROUPS.some(([value]) => value === group) ? group as AuthorGroupBy : 'topic'
  const date_from = params.get('date_from') ?? ''
  const date_to = params.get('date_to') ?? ''
  const period: AuthorPeriod = validAuthorRange(date_from, date_to)
    ? { date_from, date_to }
    : params.get('all_time') === 'true'
      ? { all_time: true }
      : { period_days: normalizedDays(params.get('period_days') ?? params.get('d')) }
  const rawView = params.get('metric_view')
  const view: Pick<AuthorFilters, 'metric_view'> = platform === 'instagram' && (rawView === 'legacy' || rawView === 'meta') ? { metric_view: rawView } : {}
  return { ...period, format, group_by, scope, ...view }
}

/** Те же включительные границы и предел, что у общего PeriodPicker и API. */
export function validAuthorRange(from: string, to: string, today = moscowToday()): boolean {
  return validDateRange(from, to) && to <= today && (Date.parse(to) - Date.parse(from)) / 86_400_000 < 730
}

function normalizedDays(raw: string | null): number {
  const days = Number(raw)
  return ANALYTICS_PERIODS.some((allowed) => allowed === days) ? days : 90
}

export function authorPeriodPatch(period: AuthorPeriod): Record<string, string | number | null> {
  return {
    d: null,
    period_days: period.period_days ?? null,
    date_from: period.date_from ?? null,
    date_to: period.date_to ?? null,
    all_time: period.all_time ? 'true' : null,
  }
}

export function authorPane(params: URLSearchParams): AuthorPane | null {
  const value = params.get('insight')
  return value === 'portrait' || value === 'group' || value === 'evidence' || value === 'annotation' || value === 'topic' ? value : null
}

export function nonNegativeId(value: string | null): number | null {
  const id = Number(value)
  return Number.isSafeInteger(id) && id > 0 ? id : null
}

/** Доли не создаются из неизвестного/нулевого знаменателя. */
export function fractionText(numerator: number, denominator: number): string {
  return denominator > 0
    ? `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(numerator / denominator * 100)}%`
    : '—'
}

export const PORTRAIT_FIELDS = [
  ['audience', 'Для кого'],
  ['task', 'Какую задачу помогает решить'],
  ['promise', 'Что обещает'],
  ['features', 'Характерные особенности'],
] as const
export type PortraitFieldKey = (typeof PORTRAIT_FIELDS)[number][0]

export function safeExternalUrl(value: string | null | undefined): string | null {
  if (!value) return null
  try {
    const url = new URL(value)
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null
  } catch {
    return null
  }
}
